import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@/components/ui/Button';
import useConsentContext from '@/contexts/Consent/useConsentContext';

/**
 * באנר העוגיות של האזור האישי (HAT-1081), פורט של הבאנר שנבנה ב-HAT-1067 לאתר
 * התדמית. מוצג לכל מי שטרם בחר, וניתן לפתיחה מחדש כדי לשנות בחירה. שום אחסון
 * מדידה לא נכתב לפני הבחירה: את הכלים מפעיל ConsentProvider רק אחרי שנרשם "כן".
 *
 * הבאנר במכוון אינו מודאלי: העמוד נשאר קריא ושמיש מאחוריו. הפוקוס נכנס אליו רק
 * כשנפתח מחדש מקישור, וחוזר לפותח בסגירה.
 *
 * HAT-1185: העיצוב הוחלף לרצועה תחתונה קומפקטית - שורה אחת קצרה + קישור למדיניות,
 * CTA ראשי אחד ולינק "הגדרות" בטקסט משני. הבחירה של "רק הכרחיות" עוברת כעת דרך
 * פאנל ההגדרות.
 *
 * HAT-1197: מזעור נוסף של הבאנר הסגור. הוסרו האייקון הגדול והכותרת הבולטת;
 * הטקסט הצטמצם לשורה קצרה עם לינק inline; ה-CTA נשאר fit-content גם במובייל
 * (~120-160px, לא מלא) כדי לשמר גובה של ≤88px אידיאלי / 100px קשיח על 390,
 * ו-≤72/88px על דסקטופ. הריווח והצל צומצמו, הפאנל של ההגדרות לא השתנה
 * בהתנהגות - אותן קטגוריות, אותה שמירה, אותה סמנטיקה של accept-all.
 */
/**
 * גובה הבאנר הפתוח, כמשתנה CSS על <html>. הצפים בפינה השמאלית התחתונה (כפתור
 * הנגישות ב-a11y/accessibility-widget.css, כפתור הדיווח הטכני
 * ב-SprintsReportWidget.module.scss) קוראים את המשתנה כדי לעלות מעל הבאנר בזמן
 * שהוא פתוח, ולשמור על env(safe-area-inset-bottom) כשהוא סגור. ה-CSS שלהם משתמש
 * ב-max() בין שני המצבים, ולכן המשתנה כאן הוא פשוט offsetHeight מלא של הבאנר -
 * כולל ה-padding התחתון שסופג את ה-safe area.
 */
const BANNER_HEIGHT_CSS_VAR = '--cookie-banner-height';

const CookieConsentBanner = () => {
  const { consent, isNoticeOpen, openNotice, closeNotice, decide, isAnalyticsRunning } = useConsentContext();
  const { t } = useTranslation();
  const [isDetailed, setIsDetailed] = useState(false);
  /* HAT-1197 (product follow-up): the settings-panel checkbox defaults to TRUE
     for a visitor who has not decided yet. People do not manually tick a
     measurement box, and Save-without-changes should leave measurement ON -
     otherwise "הגדרות → שמירת ההגדרות" silently opts everyone out. This is a
     UX default only: nothing runs before the visitor actually saves (or clicks
     the top-level "אישור"), and refusing is still one uncheck + Save away.
     Once a decision exists (`consent !== null`), we mirror it exactly. */
  const [allowAnalytics, setAllowAnalytics] = useState(consent?.analytics ?? true);
  const dialogRef = useRef<HTMLDivElement>(null);
  const reopenChipRef = useRef<HTMLButtonElement>(null);
  /* Focus restore על סגירה. שני מסלולים:
     - openedFromChipRef: המשתמש פתח את הבאנר מהצ'יפ. הצ'יפ מוסר מה-DOM ברגע
       שהבאנר עולה, ולכן document.activeElement כבר יהיה body בזמן שהאפקט של
       ה-open ירוץ - הכיוון היחיד להחזיר את הפוקוס הוא לצ'יפ שיתמוונט מחדש
       על סגירה. הדגל נדלק בתוך ה-onClick, לפני שקוראים ל-openNotice, כדי
       שהמידע יישמר לפני שהאלמנט נעלם.
     - externalOpenerRef: פתיחה מלינק חיצוני (למשל "הגדרות עוגיות" ב-privacy
       policy). האלמנט נשאר ב-DOM כשהבאנר נפתח, ולכן document.activeElement
       תופס אותו בזמן האפקט. חוזרים אליו בסגירה. */
  const openedFromChipRef = useRef(false);
  const externalOpenerRef = useRef<HTMLElement | null>(null);

  const hasDecidedBefore = consent !== null;

  const handleReopenFromChip = () => {
    openedFromChipRef.current = true;
    externalOpenerRef.current = null;
    openNotice();
  };

  useLayoutEffect(() => {
    const banner = dialogRef.current;
    if (!isNoticeOpen || !banner) return;

    const root = document.documentElement;
    const publishHeight = () => root.style.setProperty(BANNER_HEIGHT_CSS_VAR, `${banner.offsetHeight}px`);
    publishHeight();

    // הגובה משתנה עם פתיחת פאנל ההגדרות, עם שינוי רוחב המסך ועם סיבוב המכשיר.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publishHeight) : null;
    observer?.observe(banner);

    return () => {
      observer?.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_CSS_VAR);
    };
  }, [isNoticeOpen]);

  // Reset state when the notice is (re)opened, so the details panel closes and
  // the analytics checkbox reflects the current consent snapshot each time.
  useEffect(() => {
    if (!isNoticeOpen) return;
    setIsDetailed(false);
    // Same default as the initial state: opted-in by default before a decision,
    // exact mirror of the stored decision once one exists. See the comment on
    // the useState above for the product rationale (HAT-1197 follow-up).
    setAllowAnalytics(consent?.analytics ?? true);
  }, [isNoticeOpen, consent]);

  // Focus management. הפוקוס נכנס לבאנר רק כשנפתח מחדש (openedFromChipRef או
  // externalOpenerRef); בביקור ראשון הבאנר מופיע מעצמו ואסור לו לגנוב את הפוקוס
  // מקישור הדילוג ש-App.tsx שומר ראשון בכוונה. בסגירה מחזירים את הפוקוס לאיפה
  // שהמשתמש ביקש - הצ'יפ שהתמוונט מחדש, או האלמנט החיצוני שנשאר קיים.
  useEffect(() => {
    if (!hasDecidedBefore) return;

    if (isNoticeOpen) {
      if (!openedFromChipRef.current) {
        const opener = document.activeElement;
        externalOpenerRef.current = opener instanceof HTMLElement && opener !== document.body ? opener : null;
      }
      dialogRef.current?.focus();
      return;
    }

    if (openedFromChipRef.current) {
      openedFromChipRef.current = false;
      // הצ'יפ עכשיו מוונט מחדש (isNoticeOpen=false → הרנדר מחזיר button); useEffect
      // רץ אחרי commit, ולכן reopenChipRef.current מצביע כבר לצ'יפ החדש.
      reopenChipRef.current?.focus();
      return;
    }

    if (externalOpenerRef.current) {
      externalOpenerRef.current.focus();
      externalOpenerRef.current = null;
    }
  }, [isNoticeOpen, hasDecidedBefore]);

  useEffect(() => {
    if (!isNoticeOpen || !hasDecidedBefore) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeNotice();
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isNoticeOpen, hasDecidedBefore, closeNotice]);

  // אחרי בחירה נשאר פקד קטן וקבוע: מדיניות הפרטיות מבטיחה שניתן לחזור
  // מההסכמה בכל עת, ולאזור האישי אין footer לתלות בו קישור. ממוקם בצד הנגדי
  // ל-FeedbackButton ולווידג'ט הנגישות, ששניהם בפינה השמאלית התחתונה.
  if (!isNoticeOpen) {
    return (
      <button
        ref={reopenChipRef}
        type="button"
        onClick={handleReopenFromChip}
        // --font-body-2 (#616060 ≈ 6.2:1 על לבן) עומד ב-WCAG AA לטקסט רגיל.
        // --font-sub-title (#888888) שהיה כאן קודם היה מתחת ל-4.5:1.
        className="fixed bottom-2 right-3 z-[60] rounded-full border border-(--border-main) bg-white/90 px-2.5 py-1 text-xs font-semibold text-(--font-body-2) hover:text-(--font-primary) hover:bg-white transition-colors"
      >
        {t('cookieConsent.settings')}
      </button>
    );
  }

  /* z-index 80: מתחת לכפתור הנגישות (1200) ולתפריט שלו (1201), מעל תוכן העמוד.
     ה-CSS של הצפים משתמש ב-max() בין `24px + env(safe-area)` (כשסגור) ל-`24px +
     var(--cookie-banner-height)` (כשפתוח), ולכן ה-var כאן הוא פשוט הגובה המלא
     של הבאנר כולל ה-padding שסופג את ה-safe area. אין ספירה כפולה של ה-safe
     area. max-h-[70dvh] וגלילה פנימית שומרים על פאנל ההגדרות בתוך הבאנר.

     HAT-1197: הפדינג ירד ל-py:8-10 / px:12-16 והצל הוחלש (0_-1px_8px). אין
     יותר אייקון עוגייה ואין כותרת בולטת - רק שורת טקסט אחת + לינק inline
     למדיניות, ובצד ה-CTA "אישור" (fit-content ~120-160px) ולינק "הגדרות". */
  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      role="dialog"
      aria-labelledby="cookie-consent-title"
      className="fixed bottom-0 inset-x-0 z-[80] max-h-[70dvh] overflow-y-auto bg-white border-t border-(--border-main) shadow-[0_-1px_8px_rgba(19,47,83,0.08)] focus:outline-none pt-2 pb-[max(8px,env(safe-area-inset-bottom))] px-3 sm:px-4 md:px-6"
    >
      {/* שורה יחידה במובייל וגם בדסקטופ: הטקסט משמאל (RTL: מימין), הפעולות מימין.
          items-center מיישר את שני הצדדים אנכית, כך שכפתור בגובה 40px מתמרכז
          מול טקסט שיכול לגלוש לשתי שורות בטלפון צר. gap-2 קטן במיוחד כדי לחסוך
          כל פיקסל בגובה הבאנר הסגור. */}
      <div className="w-full max-w-[1400px] mx-auto flex flex-row items-center gap-2 sm:gap-4">
        {/* --font-body-2 (#616060 ≈ 6.2:1 על לבן, עומד ב-WCAG AA לטקסט רגיל).
            body-sm-regular = 14px, סף הגודל שנדרש בברייף. leading-snug מהדק את
            הרווח בין השורות במקרה שהטקסט נגלש לשתי שורות בטלפון צר. */}
        <p id="cookie-consent-title" className="body-sm-regular text-(--font-body-2) leading-snug flex-1 min-w-0">
          {t('cookieConsent.shortNotice')}{' '}
          {/* קישור רגיל ולא Link של react-router: הבאנר מרונדר מחוץ ל-BrowserRouter
              כדי לכסות גם את העמודים הציבוריים. */}
          <a
            href={`${import.meta.env.BASE_URL}privacy-policy`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-semibold text-(--font-primary) hover:text-(--surface-primary) transition-colors"
          >
            {t('cookieConsent.privacyPolicyLink')}
          </a>
        </p>

        {/* CTA ראשי אחד ("אישור" - accept-all, סמנטיקה זהה לגמרי) + לינק הגדרות
            בטקסט משני. שני הפקדים fit-content: לא לוקחים את כל הרוחב, גם לא
            במובייל, כדי לשמור את הבאנר קומפקטי. הבחירה של "רק הכרחיות" עוברת
            דרך פאנל ההגדרות - טאפ נוסף אחד, בתמורה לרצועה שלא חוסמת יותר משליש
            מהמסך. */}
        <div className="flex flex-row items-center gap-2 sm:gap-3 flex-shrink-0">
          {/* min-w 120 במובייל, 140 מ-sm ומעלה - נכנס גם במסך צר של 390 עם
              טקסט שנגלש לשתי שורות. !min-w וה-!h דורסים את ברירת המחדל של
              Button (min-w 100, h 40) בלי לשנות את ui.module.css הכללי. */}
          <Button
            variant="primary"
            className="!min-w-[120px] sm:!min-w-[140px] !h-10 !px-4"
            onClick={() => decide(true)}
          >
            {t('cookieConsent.accept')}
          </Button>
          {/* --font-body-2 (#616060) על רקע לבן = כ-6.7:1, מעל רצף ה-4.5:1 של WCAG AA
              עבור טקסט רגיל בגודל 14px. min-h 40 כדי לשמר יעד hit-target בטוח
              גם כשזה טקסט-לינק ולא כפתור מלא. */}
          <button
            type="button"
            onClick={() => setIsDetailed((isOpen) => !isOpen)}
            aria-expanded={isDetailed}
            aria-controls="cookie-consent-preferences"
            className="min-h-[40px] px-1.5 body-sm-semibold text-(--font-body-2) underline hover:text-(--font-primary) transition-colors whitespace-nowrap"
          >
            {t('cookieConsent.settings')}
          </button>
          {/* HAT-1197 (Astra follow-up): כפתור הסגירה יושב בתוך שורת הפעולות
              של הבאנר הפתוח מחדש (hasDecidedBefore), כילד flex אחרון. הגרסה
              הקודמת (absolute top/end) חפפה את "הגדרות עוגיות" ב-RTL כי גם
              הלינק וגם ה-X נחתו על הפינה השמאלית-עליונה של הבאנר. עכשיו הם
              siblings של flex, ולכן ה-layout מבטיח שאין חפיפה. min-w/h 40 =
              hit target בטוח, aria-label נשאר "סגירת חלון הגדרות העוגיות". */}
          {hasDecidedBefore && (
            <button
              type="button"
              onClick={closeNotice}
              aria-label={t('cookieConsent.close')}
              className="inline-flex items-center justify-center min-w-[40px] min-h-[40px] p-1.5 rounded-full text-(--font-body-2) hover:text-(--font-primary) hover:bg-(--surface-base) transition-colors"
            >
              <CloseIcon fontSize="small" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {isDetailed && (
        <div id="cookie-consent-preferences" className="w-full max-w-[1400px] mx-auto mt-3 pt-3 border-t border-(--border-main) flex flex-col gap-4">
          {/* התיאור המלא של השימוש בעוגיות חי כאן ולא בשורה הראשית - הבאנר הסגור
              נשאר קצר, המשתמש מרחיב רק כשהוא רוצה לראות פרטים או לסרב לאנליטיקה. */}
          <p className="body-sm-regular text-(--font-body-2) leading-relaxed">
            {t('cookieConsent.description')}
          </p>
          {/* נאמר בתוך פאנל ההגדרות כי כאן נמצא הכפתור שמוריד את המדידה - חזרה
              מהסכמה טוענת את העמוד מחדש, וההודעה מוצגת רק כשכלי באמת רץ. */}
          {/* --font-body-2 (#616060 ≈ 6.2:1) במקום --font-sub-title (#888) שהיה מתחת ל-AA.
              בלי opacity-80 - שכבה חצי-שקופה מורידה את הקונטרסט הסופי מתחת לרצף גם על
              #616060, ואת ההודעה חשוב לקרוא: היא מסבירה שהעמוד ייטען מחדש. */}
          {isAnalyticsRunning() && <p className="body-sm-regular text-(--font-body-2) leading-relaxed">{t('cookieConsent.reloadWarning')}</p>}
          <CookieCategory
            title={t('cookieConsent.necessaryTitle')}
            lockedLabel={t('cookieConsent.alwaysOn')}
            description={t('cookieConsent.necessaryDescription')}
            checked
            locked
          />
          <CookieCategory
            title={t('cookieConsent.analyticsTitle')}
            description={t('cookieConsent.analyticsDescription')}
            checked={allowAnalytics}
            onChange={setAllowAnalytics}
          />
          <div className="flex flex-wrap gap-3 items-center">
            <Button variant="primary" onClick={() => decide(allowAnalytics)}>
              {t('cookieConsent.save')}
            </Button>
            {hasDecidedBefore && (
              <button
                type="button"
                onClick={closeNotice}
                className="h-[35px] px-3 body-sm-semibold text-(--font-body-2) underline hover:text-(--font-primary) transition-colors"
              >
                {t('cookieConsent.cancel')}
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

interface CookieCategoryProps {
  title: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  lockedLabel?: string;
  onChange?: (checked: boolean) => void;
}

/* opacity-70 שהיה על ה-label של קטגוריה נעולה מוריד את הקונטרסט של הטקסט
   שכבר יושב על --font-body-2, ולכן דילגתי עליו. את "נעילה" מסמנים במקום זאת
   דרך ה-checkbox המנוטרל ולייבל "(פעילות תמיד)" - שני סמנים ויזואליים אחרים
   שלא מחייבים הורדת קונטרסט. תיאור וכותרת הקטגוריה משתמשים ב-body-2/primary
   (≈6.2 ו-≈8.9 קונטרסט), שניהם AA. */
const CookieCategory = ({ title, description, checked, locked = false, lockedLabel, onChange }: CookieCategoryProps) => (
  <label className={`flex items-start gap-3 ${locked ? '' : 'cursor-pointer'}`}>
    <input
      type="checkbox"
      checked={checked}
      disabled={locked}
      onChange={(event) => onChange?.(event.target.checked)}
      className="mt-0.5 w-4 h-4 flex-shrink-0 accent-(--font-primary) disabled:cursor-not-allowed"
    />
    <span className="flex flex-col gap-0.5">
      <span className="body-sm-bold text-(--font-primary)">
        {title}
        {locked && lockedLabel && <span className="font-normal text-(--font-body-2)"> {lockedLabel}</span>}
      </span>
      <span className="body-sm-regular text-(--font-body-2) leading-relaxed">{description}</span>
    </span>
  </label>
);

export default CookieConsentBanner;
